import { useEffect, useState } from 'react'
import {
  Header, HeaderLogo, HeaderNav, HeaderNavLink, HeaderActions,
  Footer, Button, GlassCard
} from '@lukhas/ui'
import {
  Activity, Shield, Brain, AlertCircle, CheckCircle,
  Zap, Database, Lock, TrendingUp, Server,
  BarChart3
} from 'lucide-react'

interface SystemMetrics {
  phi: number
  phiEma: number
  status: 'OPTIMAL' | 'GOOD' | 'DEGRADED' | 'CRITICAL'
  components: {
    meshConnectivity: number
    messageEntropy: number
    stateEntropy: number
    causalBidirectionality: number
  }
  safety: {
    runawayDetection: boolean
    memoryGuard: boolean
    piiProtection: boolean
    emergencyCount: number
  }
  identity: {
    activeUsers: number
    mfaEnabled: number
    breachesDetected: number
    riskScore: number
  }
}

/**
 * MATRIZ System Dashboard - Real-time monitoring of all critical systems
 */
export default function MATRIZDashboard() {
  const [metrics, setMetrics] = useState<SystemMetrics>({
    phi: 0.92,
    phiEma: 0.89,
    status: 'OPTIMAL',
    components: {
      meshConnectivity: 0.95,
      messageEntropy: 0.88,
      stateEntropy: 0.91,
      causalBidirectionality: 0.87
    },
    safety: {
      runawayDetection: true,
      memoryGuard: true,
      piiProtection: true,
      emergencyCount: 0
    },
    identity: {
      activeUsers: 1247,
      mfaEnabled: 89,
      breachesDetected: 0,
      riskScore: 12
    }
  })

  const [isLive, setIsLive] = useState(true)

  // Simulate real-time updates
  useEffect(() => {
    if (!isLive) return

    const interval = setInterval(() => {
      setMetrics(prev => ({
        ...prev,
        phi: Math.min(1, Math.max(0, prev.phi + (Math.random() - 0.5) * 0.02)),
        phiEma: Math.min(1, Math.max(0, prev.phiEma + (Math.random() - 0.5) * 0.01)),
        components: {
          meshConnectivity: Math.min(1, Math.max(0, prev.components.meshConnectivity + (Math.random() - 0.5) * 0.03)),
          messageEntropy: Math.min(1, Math.max(0, prev.components.messageEntropy + (Math.random() - 0.5) * 0.02)),
          stateEntropy: Math.min(1, Math.max(0, prev.components.stateEntropy + (Math.random() - 0.5) * 0.02)),
          causalBidirectionality: Math.min(1, Math.max(0, prev.components.causalBidirectionality + (Math.random() - 0.5) * 0.03))
        },
        identity: {
          ...prev.identity,
          activeUsers: prev.identity.activeUsers + Math.floor(Math.random() * 3 - 1),
          riskScore: Math.min(100, Math.max(0, prev.identity.riskScore + (Math.random() - 0.5) * 2))
        }
      }))
    }, 2000)

    return () => clearInterval(interval)
  }, [isLive])

  // Update status based on phi
  useEffect(() => {
    let newStatus: SystemMetrics['status']
    if (metrics.phiEma >= 0.8) newStatus = 'OPTIMAL'
    else if (metrics.phiEma >= 0.6) newStatus = 'GOOD'
    else if (metrics.phiEma >= 0.3) newStatus = 'DEGRADED'
    else newStatus = 'CRITICAL'

    if (newStatus !== metrics.status) {
      setMetrics(prev => ({ ...prev, status: newStatus }))
    }
  }, [metrics.phiEma, metrics.status])

  const getStatusColor = (status: SystemMetrics['status']) => {
    switch (status) {
      case 'OPTIMAL': return 'text-green-400 bg-green-400/10 border-green-400/30'
      case 'GOOD': return 'text-blue-400 bg-blue-400/10 border-blue-400/30'
      case 'DEGRADED': return 'text-yellow-400 bg-yellow-400/10 border-yellow-400/30'
      case 'CRITICAL': return 'text-red-400 bg-red-400/10 border-red-400/30'
    }
  }

  const getStatusIcon = (status: SystemMetrics['status']) => {
    switch (status) {
      case 'OPTIMAL':
      case 'GOOD':
        return <CheckCircle className="w-5 h-5" />
      case 'DEGRADED':
        return <AlertCircle className="w-5 h-5" />
      case 'CRITICAL':
        return <AlertCircle className="w-5 h-5 animate-pulse" />
    }
  }

  return (
    <div className="min-h-screen bg-consciousness-deep">
      {/* Header */}
      <Header className="fixed top-0 left-0 right-0 z-50 bg-consciousness-deep/80 backdrop-blur-md border-b border-code-cyan/20">
        <HeaderLogo href="/">
          <span className="text-2xl font-light tracking-[0.15em] text-awareness-silver">
            MATRIZ<span className="text-code-cyan">.MONITOR</span>
          </span>
        </HeaderLogo>
        <HeaderNav>
          <HeaderNavLink href="/docs">Docs</HeaderNavLink>
          <HeaderNavLink href="/api-reference">API</HeaderNavLink>
          <HeaderNavLink href="/playground">Playground</HeaderNavLink>
        </HeaderNav>
        <HeaderActions>
          <Button
            variant={isLive ? "default" : "ghost"}
            onClick={() => setIsLive(!isLive)}
            className="flex items-center gap-2"
          >
            <div className={`w-2 h-2 rounded-full ${isLive ? 'bg-green-400 animate-pulse' : 'bg-gray-400'}`} />
            {isLive ? 'Live' : 'Paused'}
          </Button>
        </HeaderActions>
      </Header>

      <main className="pt-24 px-8 pb-16">
        {/* System Status Banner */}
        <div className={`mb-8 p-6 rounded-lg border ${getStatusColor(metrics.status)}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {getStatusIcon(metrics.status)}
              <div>
                <h2 className="text-2xl font-bold">System Status: {metrics.status}</h2>
                <p className="text-sm opacity-75 mt-1">
                  Φ Coherence: {(metrics.phiEma * 100).toFixed(1)}% | All safety systems operational
                </p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-3xl font-mono font-bold">{(metrics.phi * 100).toFixed(1)}%</div>
              <div className="text-xs opacity-75">Current Φ</div>
            </div>
          </div>
        </div>

        {/* Metrics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Coherence Components */}
          <GlassCard className="p-6">
            <div className="flex items-center justify-between mb-4">
              <Brain className="w-8 h-8 text-code-cyan" />
              <span className="text-2xl font-bold">{(metrics.components.meshConnectivity * 100).toFixed(0)}%</span>
            </div>
            <h3 className="text-lg font-semibold mb-2">Mesh Connectivity</h3>
            <div className="w-full h-2 bg-gray-700 rounded-full overflow-hidden">
              <div
                className="h-full bg-code-cyan transition-all duration-500"
                style={{ width: `${metrics.components.meshConnectivity * 100}%` }}
              />
            </div>
          </GlassCard>

          <GlassCard className="p-6">
            <div className="flex items-center justify-between mb-4">
              <Activity className="w-8 h-8 text-awareness-purple" />
              <span className="text-2xl font-bold">{(metrics.components.messageEntropy * 100).toFixed(0)}%</span>
            </div>
            <h3 className="text-lg font-semibold mb-2">Message Entropy</h3>
            <div className="w-full h-2 bg-gray-700 rounded-full overflow-hidden">
              <div
                className="h-full bg-awareness-purple transition-all duration-500"
                style={{ width: `${metrics.components.messageEntropy * 100}%` }}
              />
            </div>
          </GlassCard>

          <GlassCard className="p-6">
            <div className="flex items-center justify-between mb-4">
              <Database className="w-8 h-8 text-quantum-gold" />
              <span className="text-2xl font-bold">{(metrics.components.stateEntropy * 100).toFixed(0)}%</span>
            </div>
            <h3 className="text-lg font-semibold mb-2">State Entropy</h3>
            <div className="w-full h-2 bg-gray-700 rounded-full overflow-hidden">
              <div
                className="h-full bg-quantum-gold transition-all duration-500"
                style={{ width: `${metrics.components.stateEntropy * 100}%` }}
              />
            </div>
          </GlassCard>

          <GlassCard className="p-6">
            <div className="flex items-center justify-between mb-4">
              <Zap className="w-8 h-8 text-green-400" />
              <span className="text-2xl font-bold">{(metrics.components.causalBidirectionality * 100).toFixed(0)}%</span>
            </div>
            <h3 className="text-lg font-semibold mb-2">Causal Flow</h3>
            <div className="w-full h-2 bg-gray-700 rounded-full overflow-hidden">
              <div
                className="h-full bg-green-400 transition-all duration-500"
                style={{ width: `${metrics.components.causalBidirectionality * 100}%` }}
              />
            </div>
          </GlassCard>
        </div>

        {/* Safety Systems */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          <GlassCard className="p-6">
            <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Shield className="w-6 h-6 text-code-cyan" />
              Safety Systems
            </h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg">
                <span>Runaway Detection</span>
                <div className={`px-3 py-1 rounded ${metrics.safety.runawayDetection ? 'bg-green-400/20 text-green-400' : 'bg-red-400/20 text-red-400'}`}>
                  {metrics.safety.runawayDetection ? 'ACTIVE' : 'INACTIVE'}
                </div>
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg">
                <span>Memory Guard</span>
                <div className={`px-3 py-1 rounded ${metrics.safety.memoryGuard ? 'bg-green-400/20 text-green-400' : 'bg-red-400/20 text-red-400'}`}>
                  {metrics.safety.memoryGuard ? 'ACTIVE' : 'INACTIVE'}
                </div>
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg">
                <span>PII Protection</span>
                <div className={`px-3 py-1 rounded ${metrics.safety.piiProtection ? 'bg-green-400/20 text-green-400' : 'bg-red-400/20 text-red-400'}`}>
                  {metrics.safety.piiProtection ? 'ACTIVE' : 'INACTIVE'}
                </div>
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg">
                <span>Emergency Events</span>
                <div className="px-3 py-1 rounded bg-blue-400/20 text-blue-400">
                  {metrics.safety.emergencyCount}
                </div>
              </div>
            </div>
          </GlassCard>

          <GlassCard className="p-6">
            <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Lock className="w-6 h-6 text-awareness-purple" />
              Identity & Security
            </h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg">
                <span>Active Users</span>
                <div className="text-xl font-mono">{metrics.identity.activeUsers.toLocaleString()}</div>
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg">
                <span>MFA Coverage</span>
                <div className="flex items-center gap-2">
                  <div className="w-20 h-2 bg-gray-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-green-400"
                      style={{ width: `${metrics.identity.mfaEnabled}%` }}
                    />
                  </div>
                  <span className="text-sm">{metrics.identity.mfaEnabled}%</span>
                </div>
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg">
                <span>Breach Attempts</span>
                <div className={`px-3 py-1 rounded ${metrics.identity.breachesDetected === 0 ? 'bg-green-400/20 text-green-400' : 'bg-red-400/20 text-red-400'}`}>
                  {metrics.identity.breachesDetected}
                </div>
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg">
                <span>Risk Score</span>
                <div className="flex items-center gap-2">
                  <div className="w-20 h-2 bg-gray-700 rounded-full overflow-hidden">
                    <div
                      className={`h-full transition-all ${
                        metrics.identity.riskScore < 30 ? 'bg-green-400' :
                        metrics.identity.riskScore < 60 ? 'bg-yellow-400' : 'bg-red-400'
                      }`}
                      style={{ width: `${metrics.identity.riskScore}%` }}
                    />
                  </div>
                  <span className="text-sm">{metrics.identity.riskScore.toFixed(0)}</span>
                </div>
              </div>
            </div>
          </GlassCard>
        </div>

        {/* Performance Metrics */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <GlassCard className="p-6">
            <div className="flex items-center justify-between mb-4">
              <Server className="w-6 h-6 text-code-cyan" />
              <span className="text-xs px-2 py-1 bg-green-400/20 text-green-400 rounded">Healthy</span>
            </div>
            <h3 className="text-lg font-semibold mb-2">API Gateway</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="opacity-75">Requests/sec</span>
                <span className="font-mono">1,247</span>
              </div>
              <div className="flex justify-between">
                <span className="opacity-75">Avg Latency</span>
                <span className="font-mono">23ms</span>
              </div>
              <div className="flex justify-between">
                <span className="opacity-75">Error Rate</span>
                <span className="font-mono text-green-400">0.02%</span>
              </div>
            </div>
          </GlassCard>

          <GlassCard className="p-6">
            <div className="flex items-center justify-between mb-4">
              <BarChart3 className="w-6 h-6 text-awareness-purple" />
              <span className="text-xs px-2 py-1 bg-green-400/20 text-green-400 rounded">Optimal</span>
            </div>
            <h3 className="text-lg font-semibold mb-2">Audit System</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="opacity-75">Events/hour</span>
                <span className="font-mono">45.2K</span>
              </div>
              <div className="flex justify-between">
                <span className="opacity-75">Storage Used</span>
                <span className="font-mono">127GB</span>
              </div>
              <div className="flex justify-between">
                <span className="opacity-75">Compliance</span>
                <span className="font-mono text-green-400">100%</span>
              </div>
            </div>
          </GlassCard>

          <GlassCard className="p-6">
            <div className="flex items-center justify-between mb-4">
              <TrendingUp className="w-6 h-6 text-quantum-gold" />
              <span className="text-xs px-2 py-1 bg-blue-400/20 text-blue-400 rounded">Scaling</span>
            </div>
            <h3 className="text-lg font-semibold mb-2">Infrastructure</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="opacity-75">CPU Usage</span>
                <span className="font-mono">67%</span>
              </div>
              <div className="flex justify-between">
                <span className="opacity-75">Memory</span>
                <span className="font-mono">4.2/8GB</span>
              </div>
              <div className="flex justify-between">
                <span className="opacity-75">Nodes Active</span>
                <span className="font-mono text-green-400">12/12</span>
              </div>
            </div>
          </GlassCard>
        </div>
      </main>

      {/* Footer */}
      <Footer className="mt-16 border-t border-code-cyan/20">
        <div className="text-center py-8">
          <p className="text-sm text-awareness-silver/60">
            MATRIZ System Monitor v2.0 | Real-time Production Metrics
          </p>
          <p className="text-xs text-awareness-silver/40 mt-2">
            Powered by LUKHAS Consciousness Framework
          </p>
        </div>
      </Footer>
    </div>
  )
}
